import { useState } from 'react';
import { addHttpsIfNoScheme, isValidUrl } from '@shared';
import { advisorRequiredFields } from './useAdvisorProfile';

const maxSchedulingLinkDisplayTextLength = 100;

export function useAdvisorValidation() {
  const [errors, setErrors] = useState<Partial<Record<keyof AdvisorProfile, boolean>>>({});

  const validateForm = (advisorProfile: AdvisorProfile) => {
    const {
      schedulingLinkUrl,
      schedulingLinkDisplayText,
      defaultHealthcareInflationSourceUrl,
      defaultHealthcareInflationPercent,
      defaultHealthcareInflationSourceText,
    } = advisorProfile;

    let errors: Partial<Record<keyof AdvisorProfile, boolean>> = {};
    const errorMessages: string[] = [];

    if (
      schedulingLinkDisplayText &&
      schedulingLinkDisplayText.length > maxSchedulingLinkDisplayTextLength
    ) {
      errors.schedulingLinkDisplayText = true;
      errorMessages.push(
        `Scheduling link text must not exceed ${maxSchedulingLinkDisplayTextLength} characters`,
      );
    }

    if (defaultHealthcareInflationPercent && !defaultHealthcareInflationSourceText) {
      errors.defaultHealthcareInflationSourceText = true;
      errorMessages.push(
        'Healthcare inflation source text is required when providing an inflation rate',
      );
    }

    const validateAndFormatUrl = (
      url: string | undefined,
      fieldName: keyof Pick<
        AdvisorProfile,
        'schedulingLinkUrl' | 'defaultHealthcareInflationSourceUrl'
      >,
    ) => {
      if (!url) return true;

      let formattedUrl = url.trim();
      formattedUrl = addHttpsIfNoScheme(formattedUrl);

      if (!isValidUrl(formattedUrl)) {
        errors[fieldName] = true;
        errorMessages.push(
          `Please enter a valid URL for ${fieldName === 'schedulingLinkUrl' ? 'Scheduling Link' : 'Healthcare Inflation Source Link'}`,
        );
        return false;
      }

      advisorProfile[fieldName] = formattedUrl;
      return true;
    };

    if (schedulingLinkUrl) {
      validateAndFormatUrl(schedulingLinkUrl, 'schedulingLinkUrl');
    }

    validateAndFormatUrl(
      defaultHealthcareInflationSourceUrl,
      'defaultHealthcareInflationSourceUrl',
    );

    if (advisorProfile.advisorBusinessPhoneNumber) {
      const phoneNumber = advisorProfile.advisorBusinessPhoneNumber.trim();
      const digitsOnly = phoneNumber.replace(/[\s\-\(\)\+\.]/g, '');
      const isValidUsPhoneNumber = digitsOnly.length === 10 && /^\d{10}$/.test(digitsOnly);
      const isValidInternationalPhoneNumber =
        digitsOnly.length >= 11 && digitsOnly.length <= 15 && /^\d{11,15}$/.test(digitsOnly);

      if (digitsOnly.length > 0) {
        if (isValidUsPhoneNumber) {
        } else if (isValidInternationalPhoneNumber) {
        } else {
          errors.advisorBusinessPhoneNumber = true;
          errorMessages.push(
            'Please enter a valid phone number (10 digits for US or 11-15 digits for international)',
          );
        }
      }
    }

    // Validate business address fields
    if (advisorProfile.advisorBusinessAddress) {
      const address = advisorProfile.advisorBusinessAddress;

      // If street address is filled, require city, state, and zip
      if (address.streetAddress && address.streetAddress.trim() !== '') {
        if (!address.city || address.city.trim() === '') {
          errors.advisorBusinessAddress = true;
          errorMessages.push('City is required when street address is provided');
        }
        if (!address.state || address.state.trim() === '') {
          errors.advisorBusinessAddress = true;
          errorMessages.push('State is required when street address is provided');
        }
        if (!address.zipCode || address.zipCode.trim() === '') {
          errors.advisorBusinessAddress = true;
          errorMessages.push('ZIP code is required when street address is provided');
        }
      }

      // Validate state format (2 letters)
      if (address.state && address.state.trim() !== '' && !/^[A-Z]{2}$/.test(address.state)) {
        errors.advisorBusinessAddress = true;
        errorMessages.push('State must be a 2-letter abbreviation (e.g., CA)');
      }

      // Validate ZIP code format (5 digits or ZIP+4)
      if (
        address.zipCode &&
        address.zipCode.trim() !== '' &&
        !/^\d{5}(-\d{4})?$/.test(address.zipCode)
      ) {
        errors.advisorBusinessAddress = true;
        errorMessages.push('ZIP code must be 5 digits (12345) or ZIP+4 format (12345-6789)');
      }
    }

    // check for required fields. Necessary for when form is submitted via the unsaved changes dialog.
    advisorRequiredFields.forEach(field => {
      if (!advisorProfile[field]) {
        errors[field] = true;
        errorMessages.push(`${field} is required`);
      }
    });

    return { errors, errorMessages: errorMessages.join(', ') };
  };

  return {
    errors,
    setErrors,
    validateForm,
    maxSchedulingLinkDisplayTextLength,
  };
}
